import { ReactNode } from 'react'

import { FormHelperText, Stack } from '@mui/material'
import { type FormControlLabelProps } from '@mui/material/FormControlLabel'

import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormBridgeOptions,
  type TidalFormState,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'

import { TimePicker } from './components/TimePicker'
import { useTimeConverter } from './hooks/useTimeConverter'

export interface TimePickerProps {
  testId?: string
  label: ReactNode
  format: '24' | '12'
}

export type TimePickerFieldValue = number | undefined
const defaultComponentValue: TimePickerFieldValue = undefined

export type TimePickerFieldProps<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = TimePickerFieldValue,
> = {
  timePickerProps: TimePickerProps
  formControlLabelProps?: Partial<FormControlLabelProps>
} & TidalFormBridgeOptions<TimePickerFieldValue, TargetField, State, AcceptedFormValue>

export function TimePickerField<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = TimePickerFieldValue,
>(props: TimePickerFieldProps<TargetField, State, AcceptedFormValue>) {
  const {
    timePickerProps: { testId, label, format },
    formControlLabelProps = {},
    initialValue = defaultComponentValue as FormFieldDescriptor<State>[TargetField]['value'],
    validateOn = 'change',
    ...bridgeOptions
  } = props

  const {
    fieldInfo: {
      field: { visible },
      onChange,
      value,
      errorOrHelperText,
      showErrorAccent,
      fieldHasError,
    },
  } = useTidalFormBridge<TimePickerFieldValue, TargetField, State, AcceptedFormValue>({
    ...bridgeOptions,
    validateOn,
    initialValue,
  })

  const { convertedValue, handleChange } = useTimeConverter(value, onChange)

  if (!visible) return null

  return (
    <Stack direction="column">
      <TimePicker
        label={label}
        value={convertedValue}
        onChange={handleChange}
        testId={testId}
        format={format}
      />
      {showErrorAccent && fieldHasError && (
        <FormHelperText error>{errorOrHelperText}</FormHelperText>
      )}
    </Stack>
  )
}
